import { Eye, Pencil, Trash2 } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { FormType, UnifiedForm } from '../../types/dashboard.types';
import { StatusBadge, TypeBadge } from '../shared';

interface TableRowProps {
  form: UnifiedForm;
  onView?: (id: string, type: FormType) => void;
  onEdit?: (id: string, type: FormType) => void;
  onDelete?: (id: string, type: FormType) => void;
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const TableRow = memo(function TableRow({
  form,
  onView,
  onEdit,
  onDelete,
  showView = true,
  showEdit = true,
  showDelete = false,
}: TableRowProps) {
  const { t } = useTranslation();

  return (
    <div className="flex h-14 items-center gap-6 border-b border-border-subtle px-6">
      <div className="flex h-full w-[180px] items-center">
        <span className="text-sm font-medium text-foreground-primary">
          {form.fullName}
        </span>
      </div>
      <div className="flex h-full w-[100px] items-center">
        <TypeBadge type={form.type} />
      </div>
      <div className="flex h-full w-[120px] items-center">
        <span className="text-sm text-foreground-secondary">
          {formatDate(form.date)}
        </span>
      </div>
      <div className="flex h-full w-[140px] items-center">
        <span className="text-sm text-foreground-secondary">
          {form.filledBy === 'agent'
            ? form.filledByName || t('dashboard.table.agent')
            : t('dashboard.table.self')}
        </span>
      </div>
      <div className="flex h-full w-[110px] items-center">
        <StatusBadge status={form.status} pipelineStage={form.pipelineStage} />
      </div>
      <div className="flex h-full w-[80px] items-center gap-2">
        {showView && onView && (
          <button
            type="button"
            onClick={() => onView(form.id, form.type)}
            className="cursor-pointer text-foreground-tertiary transition-colors hover:text-foreground-primary"
          >
            <Eye className="h-[18px] w-[18px]" />
          </button>
        )}
        {showEdit && onEdit && (
          <button
            type="button"
            onClick={() => onEdit(form.id, form.type)}
            className="cursor-pointer text-foreground-tertiary transition-colors hover:text-foreground-primary"
          >
            <Pencil className="h-[18px] w-[18px]" />
          </button>
        )}
        {showDelete && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(form.id, form.type)}
            className="cursor-pointer text-foreground-tertiary transition-colors hover:text-[#EF4444]"
          >
            <Trash2 className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>
    </div>
  );
});
